<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useEditor, EditorContent } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { useCopilotMentions } from 'shared/composables/copilot/useCopilotMentions';
import CopilotMentionMenu from './CopilotMentionMenu.vue';
import CopilotMention from './extensions/copilotMention';
import {
  findMentionTrigger,
  collectMentionItems,
} from './extensions/findMentionTrigger';

const props = defineProps({
  isLoading: { type: Boolean, default: false },
  variant: { type: String, default: 'chat' },
  pendingFiles: { type: Array, default: () => [] },
  isUploading: { type: Boolean, default: false },
  assistants: { type: Array, default: () => [] },
  assistantType: { type: String, default: 'copilot' },
});

const emit = defineEmits([
  'send',
  'stop',
  'addFiles',
  'removeFile',
  'update:assistantType',
  'openPanel',
]);

const { t } = useI18n();
const fileInputRef = ref(null);
const hasComposerContent = ref(false);

const {
  mentionedItems,
  menuOpen,
  mentionQuery,
  searchResults,
  isSearching,
  selectedIndex,
  showCategories,
  showTypeBadge,
  availableCategories,
  syncMentionFromEditor,
  mentionRange,
  addMentionItem,
  pruneMentionsToKeys,
  clearMentions,
  closeMenu,
  moveSelection,
  getSelectedItem,
  selectCategory,
} = useCopilotMentions();

function handleMentionOpen(panel) {
  if (panel) emit('openPanel', panel);
}

function syncEditorMentionState() {
  if (!editor.value) return;
  const text = editor.value.getText()?.trim() || '';
  const docMentions = collectMentionItems(editor.value);
  pruneMentionsToKeys(docMentions.map(m => `${m.type}:${m.id}`));
  hasComposerContent.value =
    text.length > 0 || docMentions.length > 0 || mentionedItems.value.length > 0;
  syncMentionFromEditor(findMentionTrigger(editor.value));
  updateEditorHeight();
}

function updateEditorHeight() {
  const el = editor.value?.view?.dom;
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
}

const isHome = computed(() => props.variant === 'home');

const editor = useEditor({
  extensions: [
    StarterKit.configure({
      heading: false,
      bold: false,
      italic: false,
      strike: false,
      code: false,
      codeBlock: false,
      blockquote: false,
      bulletList: false,
      orderedList: false,
      listItem: false,
      horizontalRule: false,
      dropcursor: false,
      gapcursor: false,
    }),
    CopilotMention.configure({
      onOpen: handleMentionOpen,
    }),
    Placeholder.configure({
      placeholder: () => t('COPILOT.INPUT_PLACEHOLDER'),
      showOnlyWhenEditable: true,
      showOnlyCurrent: false,
    }),
  ],
  content: '',
  editorProps: {
    attributes: {
      class:
        'reset-base outline-none text-n-slate-12 max-h-[200px] overflow-y-auto [&_p]:m-0 [&_p.is-editor-empty:first-child]:before:pointer-events-none [&_p.is-editor-empty:first-child]:before:float-left [&_p.is-editor-empty:first-child]:before:h-0 [&_p.is-editor-empty:first-child]:before:text-n-slate-9 [&_p.is-editor-empty:first-child]:before:content-[attr(data-placeholder)]',
    },
    handleKeyDown: (_view, event) => {
      if (menuOpen.value) {
        if (event.key === 'ArrowUp') {
          event.preventDefault();
          moveSelection('up');
          return true;
        }
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          moveSelection('down');
          return true;
        }
        if (event.key === 'Enter' || event.key === 'Tab') {
          event.preventDefault();
          const selected = getSelectedItem();
          if (selected?.type === 'category') {
            selectCategory(selected.id);
          } else if (selected?.type === 'result') {
            selectMentionItem(selected.item);
          }
          return true;
        }
        if (event.key === 'Escape') {
          event.preventDefault();
          closeMenu();
          return true;
        }
      }

      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        handleSubmit();
        return true;
      }

      return false;
    },
  },
  onUpdate: () => {
    syncEditorMentionState();
  },
  onSelectionUpdate: () => {
    syncEditorMentionState();
  },
  onCreate: () => {
    nextTick(() => {
      syncEditorMentionState();
      applyEditorVariantClasses();
    });
  },
});

watch(
  () => props.variant,
  () => nextTick(applyEditorVariantClasses)
);

function applyEditorVariantClasses() {
  const el = editor.value?.view?.dom;
  if (!el) return;
  el.classList.toggle('text-base', isHome.value);
  el.classList.toggle('min-h-[52px]', isHome.value);
  el.classList.toggle('text-[15px]', !isHome.value);
  el.classList.toggle('min-h-[28px]', !isHome.value);
}

const canSend = computed(() => {
  const hasFiles = props.pendingFiles.some(f => f.fileUrl && !f.error);
  return (
    (hasComposerContent.value || hasFiles) &&
    !props.isLoading &&
    !props.isUploading
  );
});

function handleSubmit() {
  if (!canSend.value || !editor.value) return;

  const content = editor.value.getText().trim();
  const mentions = [...mentionedItems.value];
  emit('send', content, mentions);
  editor.value.commands.clearContent(true);
  clearMentions();
  hasComposerContent.value = false;
  nextTick(updateEditorHeight);
}

function selectMentionItem(item) {
  if (!editor.value || !mentionRange.value) return;

  const { start, end } = mentionRange.value;
  const subtitle = item.subtitle || item.date || '';

  editor.value
    .chain()
    .focus()
    .deleteRange({ from: start, to: end })
    .insertContent([
      {
        type: 'copilotMention',
        attrs: {
          type: item.type,
          id: item.id,
          name: item.name,
          subtitle,
          portalSlug: item.portalSlug || '',
        },
      },
      { type: 'text', text: ' ' },
    ])
    .run();

  addMentionItem(item);
  closeMenu();
  nextTick(() => {
    syncEditorMentionState();
    editor.value?.commands.focus();
  });
}

function handleMentionSelect(item) {
  selectMentionItem(item);
}

function handleCategorySelect(categoryId) {
  selectCategory(categoryId);
}

function triggerFileInput() {
  fileInputRef.value?.click();
}

function handleFileSelect(event) {
  const files = event.target.files;
  if (files?.length) {
    emit('addFiles', files);
  }
  event.target.value = '';
}

const isDragOver = ref(false);
const showAssistantMenu = ref(false);

const currentAssistant = () =>
  props.assistants.find(a => a.id === props.assistantType) ||
  props.assistants[0];

function selectAssistant(id) {
  emit('update:assistantType', id);
  showAssistantMenu.value = false;
}

function handlePointerDownOutside(event) {
  if (
    showAssistantMenu.value &&
    !event.target.closest('[data-assistant-menu]')
  ) {
    showAssistantMenu.value = false;
  }
  if (
    menuOpen.value &&
    !event.target.closest('[data-mention-menu]') &&
    !event.target.closest('.ProseMirror') &&
    !event.target.closest('[data-copilot-editor]')
  ) {
    closeMenu();
  }
}

onMounted(() =>
  document.addEventListener('mousedown', handlePointerDownOutside)
);
onBeforeUnmount(() => {
  document.removeEventListener('mousedown', handlePointerDownOutside);
  editor.value?.destroy();
});

function handleDragOver(event) {
  event.preventDefault();
  if (event.dataTransfer?.types?.includes('Files')) {
    isDragOver.value = true;
  }
}

function handleDragLeave() {
  isDragOver.value = false;
}

function handleDrop(event) {
  event.preventDefault();
  event.stopPropagation();
  isDragOver.value = false;
  const files = event.dataTransfer?.files;
  if (files?.length) {
    emit('addFiles', files);
  }
}

function clearComposer() {
  editor.value?.commands.clearContent(true);
  clearMentions();
  hasComposerContent.value = false;
  nextTick(updateEditorHeight);
}

defineExpose({ clearComposer });
</script>

<template>
  <!-- Hidden file input -->
  <input
    ref="fileInputRef"
    type="file"
    multiple
    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
    class="hidden"
    @change="handleFileSelect"
  />

  <!-- Home variant: centered card style -->
  <div v-if="variant === 'home'" class="w-full">
    <div
      class="w-full rounded-2xl bg-n-solid-2 shadow-[0_4px_8px_rgba(0,0,0,0.2)] ring-1 transition-colors"
      :class="
        isDragOver
          ? 'ring-n-brand bg-n-brand/5'
          : 'ring-n-slate-5 focus-within:ring-n-brand/60'
      "
      @dragover="handleDragOver"
      @dragleave="handleDragLeave"
      @drop="handleDrop"
    >
      <div
        class="relative flex flex-wrap items-start gap-2 px-4 pt-3 pb-2"
        data-copilot-editor
      >
        <div
          v-for="(entry, idx) in pendingFiles"
          :key="idx"
          class="relative"
        >
          <div
            v-if="entry.isImage && entry.preview"
            class="relative size-12 rounded-lg overflow-hidden ring-1 ring-n-weak"
          >
            <img
              :src="entry.preview"
              :alt="entry.name"
              class="size-full object-cover"
            />
            <div
              v-if="entry.uploading"
              class="absolute inset-0 bg-black/40 flex items-center justify-center"
            >
              <div class="i-lucide-loader-2 size-4 text-white animate-spin" />
            </div>
            <button
              type="button"
              class="absolute top-0.5 right-0.5 size-5 rounded-full bg-black/50 flex items-center justify-center hover:bg-black/70 transition-colors"
              @click="$emit('removeFile', idx)"
            >
              <div class="i-lucide-x size-3 text-white" />
            </button>
          </div>
          <div
            v-else
            class="flex items-center gap-1.5 pl-2.5 pr-1.5 py-1.5 rounded-lg bg-n-solid-3 ring-1 ring-n-weak text-xs"
          >
            <div class="i-lucide-file size-3.5 text-n-slate-11 shrink-0" />
            <span class="max-w-24 truncate text-n-slate-12">{{
              entry.name
            }}</span>
            <div
              v-if="entry.uploading"
              class="i-lucide-loader-2 size-3 text-n-slate-9 animate-spin shrink-0"
            />
            <button
              v-else
              type="button"
              class="shrink-0 flex items-center justify-center size-4 rounded text-n-slate-11 hover:text-n-slate-12 transition-colors"
              :title="entry.error || undefined"
              @click="$emit('removeFile', idx)"
            >
              <div
                class="i-lucide-x size-3"
                :class="entry.error ? 'text-n-ruby-9' : ''"
              />
            </button>
          </div>
        </div>

        <div class="relative flex-1 min-w-[12rem]">
          <CopilotMentionMenu
            v-if="menuOpen"
            :show-categories="showCategories"
            :show-type-badge="showTypeBadge"
            :categories="availableCategories"
            :items="searchResults"
            :selected-index="selectedIndex"
            :is-loading="isSearching"
            :query="mentionQuery"
            @select="handleMentionSelect"
            @select-category="handleCategorySelect"
          />
          <EditorContent :editor="editor" />
        </div>
      </div>
      <div class="flex items-center justify-between px-2.5 pb-2.5">
        <div class="flex items-center gap-1">
          <button
            type="button"
            class="flex items-center justify-center size-8 rounded-full text-n-slate-9 hover:text-n-slate-12 hover:bg-n-solid-3 transition-colors"
            :title="t('COPILOT.ATTACH_FILE')"
            @click="triggerFileInput"
          >
            <div class="i-lucide-paperclip size-4" />
          </button>
          <div
            v-if="assistants.length > 1"
            class="relative"
            data-assistant-menu
          >
            <button
              type="button"
              class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium transition-colors"
              :class="
                showAssistantMenu
                  ? 'bg-n-brand/10 text-n-brand'
                  : 'text-n-slate-9 hover:text-n-slate-12 hover:bg-n-solid-3'
              "
              @click="showAssistantMenu = !showAssistantMenu"
            >
              <div class="size-3.5" :class="currentAssistant()?.icon" />
              <span>{{ currentAssistant()?.name }}</span>
              <div class="i-lucide-chevron-down size-3" />
            </button>
            <div
              v-if="showAssistantMenu"
              class="absolute left-0 bottom-full mb-2 w-64 rounded-xl bg-n-solid-2 shadow-lg ring-1 ring-n-weak p-1.5 z-[100]"
            >
              <button
                v-for="assistant in assistants"
                :key="assistant.id"
                type="button"
                class="flex items-center gap-2.5 w-full px-3 py-2.5 rounded-lg text-left text-sm transition-colors"
                :class="
                  assistantType === assistant.id
                    ? 'bg-n-brand/10 text-n-brand'
                    : 'text-n-slate-11 hover:bg-n-solid-3 hover:text-n-slate-12'
                "
                @click="selectAssistant(assistant.id)"
              >
                <div class="size-4 shrink-0" :class="assistant.icon" />
                <div class="flex-1 min-w-0">
                  <div class="font-medium">{{ assistant.name }}</div>
                  <div class="text-xs text-n-slate-9">
                    {{ assistant.description }}
                  </div>
                </div>
                <div
                  v-if="assistantType === assistant.id"
                  class="i-lucide-check size-4 text-n-brand shrink-0"
                />
              </button>
            </div>
          </div>
        </div>
        <button
          v-if="!isLoading"
          type="button"
          class="flex items-center justify-center rounded-full size-9 shrink-0 transition-colors"
          :class="
            canSend
              ? 'bg-n-brand text-white hover:bg-n-brand-hover'
              : 'bg-n-solid-3 text-n-slate-9 cursor-not-allowed'
          "
          :disabled="!canSend"
          @click="handleSubmit"
        >
          <div class="i-lucide-arrow-up size-4" />
        </button>
        <button
          v-else
          type="button"
          class="flex items-center justify-center rounded-full size-9 shrink-0 bg-n-slate-4 text-n-slate-11 hover:bg-n-slate-5 transition-colors"
          @click="$emit('stop')"
        >
          <div class="i-lucide-square size-3.5" />
        </button>
      </div>
    </div>
  </div>

  <!-- Chat variant: bottom bar -->
  <div
    v-else
    class="pt-3"
    :class="isDragOver ? 'bg-n-brand/5' : ''"
    @dragover="handleDragOver"
    @dragleave="handleDragLeave"
    @drop="handleDrop"
  >
    <div class="w-full">
      <div
        class="rounded-2xl bg-n-solid-4 shadow-[0_4px_8px_rgba(0,0,0,0.2)] ring-1 transition-colors"
        :class="
          isDragOver
            ? 'ring-n-brand bg-n-brand/5'
            : 'ring-n-slate-5 focus-within:ring-n-brand/60'
        "
      >
        <div
          class="relative flex flex-wrap items-start gap-2 px-3 pt-2.5 pb-1.5"
          data-copilot-editor
        >
          <div
            v-for="(entry, idx) in pendingFiles"
            :key="idx"
            class="relative"
          >
            <div
              v-if="entry.isImage && entry.preview"
              class="relative size-10 rounded-lg overflow-hidden ring-1 ring-n-weak"
            >
              <img
                :src="entry.preview"
                :alt="entry.name"
                class="size-full object-cover"
              />
              <div
                v-if="entry.uploading"
                class="absolute inset-0 bg-black/40 flex items-center justify-center"
              >
                <div class="i-lucide-loader-2 size-4 text-white animate-spin" />
              </div>
              <button
                type="button"
                class="absolute top-0.5 right-0.5 size-4 rounded-full bg-black/50 flex items-center justify-center hover:bg-black/70 transition-colors"
                @click="$emit('removeFile', idx)"
              >
                <div class="i-lucide-x size-2.5 text-white" />
              </button>
            </div>
            <div
              v-else
              class="flex items-center gap-1.5 pl-2.5 pr-1.5 py-1.5 rounded-lg bg-n-solid-3 ring-1 ring-n-weak text-xs"
            >
              <div class="i-lucide-file size-3.5 text-n-slate-11 shrink-0" />
              <span class="max-w-24 truncate text-n-slate-12">{{
                entry.name
              }}</span>
              <div
                v-if="entry.uploading"
                class="i-lucide-loader-2 size-3 text-n-slate-9 animate-spin shrink-0"
              />
              <button
                v-else
                type="button"
                class="shrink-0 flex items-center justify-center size-4 rounded text-n-slate-11 hover:text-n-slate-12 transition-colors"
                :title="entry.error || undefined"
                @click="$emit('removeFile', idx)"
              >
                <div
                  class="i-lucide-x size-3"
                  :class="entry.error ? 'text-n-ruby-9' : ''"
                />
              </button>
            </div>
          </div>

          <div class="relative flex-1 min-w-[12rem]">
            <CopilotMentionMenu
              v-if="menuOpen"
              :show-categories="showCategories"
              :show-type-badge="showTypeBadge"
              :categories="availableCategories"
              :items="searchResults"
              :selected-index="selectedIndex"
              :is-loading="isSearching"
              :query="mentionQuery"
              @select="handleMentionSelect"
              @select-category="handleCategorySelect"
            />
            <EditorContent :editor="editor" />
          </div>
        </div>
        <div class="flex items-center justify-between px-2 pb-2">
          <div class="flex items-center gap-1">
            <button
              type="button"
              class="flex items-center justify-center size-8 rounded-full text-n-slate-9 hover:text-n-slate-12 hover:bg-n-solid-3 transition-colors"
              :title="t('COPILOT.ATTACH_FILE')"
              @click="triggerFileInput"
            >
              <div class="i-lucide-paperclip size-4" />
            </button>
            <div
              v-if="assistants.length > 1"
              class="relative"
              data-assistant-menu
            >
              <button
                type="button"
                class="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors"
                :class="
                  showAssistantMenu
                    ? 'bg-n-brand/10 text-n-brand'
                    : 'text-n-slate-9 hover:text-n-slate-12 hover:bg-n-solid-3'
                "
                @click="showAssistantMenu = !showAssistantMenu"
              >
                <div class="size-3" :class="currentAssistant()?.icon" />
                <span>{{ currentAssistant()?.name }}</span>
                <div class="i-lucide-chevron-down size-3" />
              </button>
              <div
                v-if="showAssistantMenu"
                class="absolute left-0 bottom-full mb-2 w-64 rounded-xl bg-n-solid-2 shadow-lg ring-1 ring-n-weak p-1.5 z-[100]"
              >
                <button
                  v-for="assistant in assistants"
                  :key="assistant.id"
                  type="button"
                  class="flex items-center gap-2.5 w-full px-3 py-2.5 rounded-lg text-left text-sm transition-colors"
                  :class="
                    assistantType === assistant.id
                      ? 'bg-n-brand/10 text-n-brand'
                      : 'text-n-slate-11 hover:bg-n-solid-3 hover:text-n-slate-12'
                  "
                  @click="selectAssistant(assistant.id)"
                >
                  <div class="size-4 shrink-0" :class="assistant.icon" />
                  <div class="flex-1 min-w-0">
                    <div class="font-medium">{{ assistant.name }}</div>
                    <div class="text-xs text-n-slate-9">
                      {{ assistant.description }}
                    </div>
                  </div>
                  <div
                    v-if="assistantType === assistant.id"
                    class="i-lucide-check size-4 text-n-brand shrink-0"
                  />
                </button>
              </div>
            </div>
          </div>
          <button
            v-if="!isLoading"
            type="button"
            class="flex items-center justify-center size-8 rounded-full transition-colors"
            :class="
              canSend
                ? 'bg-n-brand text-white hover:bg-n-brand-hover'
                : 'bg-n-solid-3 text-n-slate-9 cursor-not-allowed'
            "
            :disabled="!canSend"
            @click="handleSubmit"
          >
            <div class="i-lucide-arrow-up size-4" />
          </button>
          <div v-else class="flex items-center gap-2.5">
            <span class="text-sm font-medium text-n-slate-11">
              {{ t('COPILOT.GENERATING') }}
            </span>
            <button
              type="button"
              class="flex items-center justify-center size-8 rounded-full bg-n-ruby-3 text-n-ruby-11 hover:bg-n-ruby-4 ring-1 ring-n-ruby-6 transition-colors"
              :title="t('COPILOT.STOP_GENERATING')"
              @click="$emit('stop')"
            >
              <div class="i-lucide-square size-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
